<template>
  <div class="pe">
    <div class="pe-bar">
      <button class="pe-btn" @click="addPlacement">Add widget</button>
      <button class="pe-btn pe-primary" :disabled="saving" @click="save">{{ saving ? 'Saving…' : 'Save' }}</button>
      <button class="pe-btn" @click="$emit('cancel')">Cancel</button>
    </div>
    <div v-if="saveError" class="pe-error">{{ saveError }}</div>

    <div class="pe-stage">
      <div class="pe-stagebg" :style="stageStyle(draft)">
      <div ref="viewport" class="pe-viewport" :style="{ height: stageHeight + 'px' }">
        <div class="pe-canvas" :style="canvasStyle" @pointerdown.self="selectedId = null">
          <div
            v-for="p in draft.placements"
            :key="p.id"
            class="pe-place"
            :class="{ 'pe-selected': selectedId === p.id }"
            :style="placeStyle(p)"
            @pointerdown="selectedId = p.id"
          >
            <div class="pe-grip" @pointerdown.stop.prevent="startDrag($event, p, 'move')">{{ p.id }}</div>
            <WidgetHost
              :placement="p"
              :widget="widgetDef(p.widget)"
              :page-id="pageId"
              :spec="draft"
              :filters="values"
              :width="p.layout.w"
              :height="p.layout.h"
            />
            <div class="pe-resize" @pointerdown.stop.prevent="startDrag($event, p, 'resize')" />
          </div>
        </div>
      </div>
      </div>

      <aside class="pe-panel">
        <template v-if="selected">
          <div class="pe-panel-title">Placement</div>
          <label class="pe-field"><span>Widget</span>
            <select v-model="selected.widget" @change="onWidgetChange(selected)">
              <option v-for="w in widgetList" :key="w.slug" :value="w.slug">{{ w.name }}{{ w.stock ? '' : ' (custom)' }}</option>
            </select>
          </label>
          <div v-if="contractParams(selected).length" class="pe-hint">Reads the page variables: {{ contractParams(selected).join(', ') }}</div>
          <div v-for="inp in contractInputs(selected)" :key="inp.name" class="pe-input">
            <div class="pe-input-name">Input <b>{{ inp.name }}</b> <span v-if="inp.description">— {{ inp.description }}</span></div>
            <select :value="bindingKey(selected, inp.name)" @change="bind(selected, inp.name, $event.target.value)">
              <option value="">Choose a source…</option>
              <optgroup v-if="pageSourceNames.length" label="Page sources">
                <option v-for="n in pageSourceNames" :key="n" :value="'source:' + n">{{ n }}</option>
              </optgroup>
              <optgroup label="Governed objects">
                <option v-for="s in sourcesFor(inp)" :key="s.kind + ':' + s.id" :value="s.kind + ':' + s.id">{{ s.kind }} · {{ s.name }}</option>
              </optgroup>
            </select>
            <label v-if="ownBinding(selected, inp.name)" class="pe-field"><span>Follow variable instead of that id</span>
              <input :value="variableOf(selected, inp.name)" type="text" placeholder="metric" @change="followVariable(selected, inp.name, $event.target.value)">
            </label>
            <div v-if="inp.columns && inp.columns.length" class="pe-hint">needs {{ inp.columns.map((c) => c.name).join(', ') }}</div>
          </div>
          <label class="pe-field"><span>Config (JSON)</span>
            <textarea v-model="configText" rows="8" @blur="applyConfig" />
          </label>
          <div v-if="configError" class="pe-error">{{ configError }}</div>
          <label class="pe-field"><span>Implicit values (JSON)</span>
            <textarea v-model="implicitText" rows="3" placeholder="{&quot;metric&quot;: 51}" @blur="applyPlacementJson('implicit', implicitText, {})" />
          </label>
          <label class="pe-field"><span>Widget filters (JSON)</span>
            <textarea v-model="widgetFiltersText" rows="4" @blur="applyPlacementJson('filters', widgetFiltersText, [])" />
          </label>
          <div v-if="placementJsonError" class="pe-error">{{ placementJsonError }}</div>
          <label class="pe-field"><span>Intent</span><input v-model="selected.intent" type="text"></label>
          <div class="pe-panel-sub">Style</div>
          <label class="pe-field"><span>Background</span>
            <div class="pe-color">
              <input :value="look.background || '#ffffff'" type="color" @input="setLook('background', $event.target.value)">
              <input :value="look.background || ''" type="text" placeholder="#ffffff" @change="setLook('background', $event.target.value)">
            </div>
          </label>
          <label class="pe-field"><span>Border colour</span>
            <div class="pe-color">
              <input :value="look.border_color || '#e6e4df'" type="color" @input="setLook('border_color', $event.target.value)">
              <input :value="look.border_color || ''" type="text" placeholder="#e6e4df" @change="setLook('border_color', $event.target.value)">
            </div>
          </label>
          <div class="pe-row">
            <label class="pe-field pe-num"><span>Border</span><input :value="look.border_width ?? ''" type="number" min="0" max="12" placeholder="1" @change="setLook('border_width', $event.target.value)"></label>
            <label class="pe-field pe-num"><span>Radius</span><input :value="look.border_radius ?? ''" type="number" min="0" max="48" placeholder="8" @change="setLook('border_radius', $event.target.value)"></label>
            <label class="pe-field pe-num"><span>Padding</span><input :value="look.padding ?? ''" type="number" min="0" max="64" placeholder="0" @change="setLook('padding', $event.target.value)"></label>
          </div>
          <label class="pe-field"><span>Shadow</span>
            <select :value="look.shadow || ''" @change="setLook('shadow', $event.target.value)">
              <option value="">Default (none)</option>
              <option value="sm">Small</option>
              <option value="md">Medium</option>
            </select>
          </label>
          <div class="pe-row">
            <label class="pe-field pe-num"><span>x</span><input v-model.number="selected.layout.x" type="number"></label>
            <label class="pe-field pe-num"><span>y</span><input v-model.number="selected.layout.y" type="number"></label>
            <label class="pe-field pe-num"><span>w</span><input v-model.number="selected.layout.w" type="number"></label>
            <label class="pe-field pe-num"><span>h</span><input v-model.number="selected.layout.h" type="number"></label>
          </div>
          <label class="pe-field pe-check"><input v-model="selected.layout.grow" type="checkbox"><span>Grow to fit content (h is the minimum)</span></label>
          <button class="pe-btn pe-danger" @click="removePlacement(selected.id)">Remove</button>
        </template>
        <template v-else>
          <div class="pe-panel-title">Page</div>
          <label class="pe-field"><span>Canvas width</span><input v-model.number="draft.canvas.width" type="number" min="320" max="4000"></label>
          <label class="pe-field"><span>Background</span>
            <div class="pe-color">
              <input :value="draft.canvas.background || '#ffffff'" type="color" @input="draft.canvas.background = $event.target.value">
              <input v-model="draft.canvas.background" type="text" placeholder="transparent">
            </div>
          </label>
          <label class="pe-field"><span>Padding (px)</span><input v-model.number="draft.canvas.padding" type="number" min="0" max="200"></label>
          <label v-if="categories.length" class="pe-field"><span>Shows in</span>
            <select v-model="draftCategory">
              <option value="">Not published</option>
              <option v-for="c in categories" :key="c.key" :value="c.key">{{ c.label }}</option>
            </select>
          </label>
          <div v-if="!subject" class="pe-field"><span>Folder</span><FolderPicker v-model="draftStem" :stems="stems" /></div>
          <label v-if="subject" class="pe-field pe-check"><input v-model="draftShared" type="checkbox"><span>Shared with the organisation</span></label>
          <div v-if="subject" class="pe-hint">This is the page for {{ subject }}{{ subjectId ? ' ' + subjectId : '' }}.</div>
          <label class="pe-field"><span>Filters the viewer sees (JSON)</span>
            <textarea v-model="filtersText" rows="6" @blur="applyFilters" />
          </label>
          <div v-if="filtersError" class="pe-error">{{ filtersError }}</div>
          <label class="pe-field"><span>Implicit values (JSON): names the host supplies, null to leave to it</span>
            <textarea v-model="implicitPageText" rows="4" @blur="applyPageJson('implicit', implicitPageText, {})" />
          </label>
          <label class="pe-field"><span>Data sources (JSON): name to {kind, id, params}</span>
            <textarea v-model="sourcesText" rows="8" @blur="applyPageJson('sources', sourcesText, {})" />
          </label>
          <div v-if="pageJsonError" class="pe-error">{{ pageJsonError }}</div>
          <div class="pe-hint">Select a placement to edit it, or drag by its handle.</div>
        </template>
      </aside>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import WidgetHost from './WidgetHost.vue';
import FolderPicker from '@/components/Pages/FolderPicker.vue';
import { allStems, domainStems } from '@/utils/pageFolders';
import { canvasHeight, canvasWidth, filterDefaults, placeStyle, stageStyle } from './pageLayout';

function clone(v) {
  return JSON.parse(JSON.stringify(v));
}

export default {
  name: 'PageEditor',
  components: { WidgetHost, FolderPicker },
  props: {
    pageId: { type: Number, required: true },
    spec: { type: Object, required: true },
    widgets: { type: Object, default: () => ({}) },
    category: { type: String, default: '' },
    categories: { type: Array, default: () => [] },
    subject: { type: String, default: '' },
    subjectId: { type: Number, default: null },
    shared: { type: Boolean, default: false },
    stem: { type: String, default: '' },
  },
  emits: ['saved', 'cancel'],
  data() {
    const draft = clone(this.spec);
    return {
      draft,
      draftCategory: this.category,
      draftShared: this.shared,
      draftStem: this.stem,
      stems: [],
      implicitText: '',
      widgetFiltersText: '',
      placementJsonError: '',
      implicitPageText: JSON.stringify(draft.implicit || {}, null, 2),
      sourcesText: JSON.stringify(draft.sources || {}, null, 2),
      pageJsonError: '',
      values: filterDefaults(draft),
      selectedId: null,
      saving: false,
      saveError: '',
      viewportWidth: 0,
      widgetList: [],
      sources: [],
      configText: '',
      configError: '',
      filtersText: JSON.stringify(draft.filters || [], null, 2),
      filtersError: '',
      drag: null,
    };
  },
  computed: {
    scale() {
      if (!this.viewportWidth) return 1;
      return this.viewportWidth / canvasWidth(this.draft);
    },
    stageHeight() {
      return Math.ceil(canvasHeight(this.draft) * this.scale);
    },
    canvasStyle() {
      return {
        width: canvasWidth(this.draft) + 'px',
        height: canvasHeight(this.draft) + 'px',
        transform: `scale(${this.scale})`,
      };
    },
    selected() {
      return (this.draft.placements || []).find((p) => p.id === this.selectedId) || null;
    },
    look() {
      return (this.selected && this.selected.style) || {};
    },
    pageSourceNames() {
      return Object.keys(this.draft.sources || {});
    },
  },
  watch: {
    selectedId() {
      this.configText = this.selected ? JSON.stringify(this.selected.config || {}, null, 2) : '';
      this.implicitText = this.selected ? JSON.stringify(this.selected.implicit || {}, null, 2) : '';
      this.widgetFiltersText = this.selected ? JSON.stringify(this.selected.filters || [], null, 2) : '';
      this.configError = '';
      this.placementJsonError = '';
    },
  },
  async mounted() {
    this.measure();
    window.addEventListener('resize', this.measure);
    window.addEventListener('pointermove', this.onDrag);
    window.addEventListener('pointerup', this.endDrag);
    const [{ data: w }, { data: s }, { data: pages }, domains] = await Promise.all([
      axios.get('/api/impact_analytics/widgets/'),
      axios.get('/api/impact_analytics/agent-pages/sources/'),
      axios.get('/api/impact_analytics/agent-pages/'),
      domainStems(),
    ]);
    this.widgetList = w.widgets || [];
    this.sources = s.sources || [];
    this.stems = [...new Set([...domains, ...allStems(pages.filter((page) => page.mine && !page.subject_type))])];
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.measure);
    window.removeEventListener('pointermove', this.onDrag);
    window.removeEventListener('pointerup', this.endDrag);
  },
  methods: {
    placeStyle,
    stageStyle,
    measure() {
      this.viewportWidth = this.$refs.viewport ? this.$refs.viewport.clientWidth : 0;
    },
    widgetDef(slug) {
      return this.widgetList.find((w) => w.slug === slug) || this.widgets[slug] || null;
    },
    contractInputs(p) {
      const w = this.widgetDef(p.widget);
      return ((w && w.contract) || {}).inputs || [];
    },
    sourcesFor(inp) {
      const kinds = inp.kinds && inp.kinds.length ? inp.kinds : null;
      return this.sources.filter((s) => !kinds || kinds.includes(s.kind));
    },
    contractParams(p) {
      const w = this.widgetDef(p.widget);
      return (((w && w.contract) || {}).params || []).map((x) => x.name);
    },
    bindingKey(p, name) {
      const b = (p.inputs || {})[name];
      if (!b) return '';
      return b.source ? `source:${b.source}` : `${b.kind}:${typeof b.id === 'object' ? '' : b.id}`;
    },
    ownBinding(p, name) {
      const b = (p.inputs || {})[name];
      return !!b && !b.source;
    },
    variableOf(p, name) {
      const b = (p.inputs || {})[name];
      return b && b.id && typeof b.id === 'object' ? b.id.var : '';
    },
    followVariable(p, name, variable) {
      const b = p.inputs[name];
      const text = String(variable || '').trim();
      if (text) b.id = { var: text };
      else delete p.inputs[name];
    },
    bind(p, name, key) {
      if (!p.inputs) p.inputs = {};
      if (!key) {
        delete p.inputs[name];
        return;
      }
      if (key.startsWith('source:')) {
        p.inputs[name] = { source: key.slice(7) };
        return;
      }
      const [kind, id] = key.split(':');
      const source = this.sources.find((s) => s.kind === kind && String(s.id) === id);
      p.inputs[name] = { kind, id: Number(id), params: {}, label: source ? source.name : '' };
    },
    onWidgetChange(p) {
      const names = new Set(this.contractInputs(p).map((i) => i.name));
      Object.keys(p.inputs || {}).forEach((n) => { if (!names.has(n)) delete p.inputs[n]; });
    },
    applyConfig() {
      if (!this.selected) return;
      try {
        const parsed = JSON.parse(this.configText || '{}');
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('Config must be a JSON object.');
        this.selected.config = parsed;
        this.configError = '';
      } catch (e) {
        this.configError = e.message;
      }
    },
    applyPlacementJson(field, text, empty) {
      if (!this.selected) return;
      try {
        const parsed = JSON.parse(text || JSON.stringify(empty));
        if (Array.isArray(parsed) !== Array.isArray(empty) || typeof parsed !== 'object' || parsed === null) {
          throw new Error(Array.isArray(empty) ? `${field} must be a JSON list.` : `${field} must be a JSON object.`);
        }
        if (Array.isArray(parsed) ? parsed.length : Object.keys(parsed).length) this.selected[field] = parsed;
        else delete this.selected[field];
        this.placementJsonError = '';
      } catch (e) {
        this.placementJsonError = e.message;
      }
    },
    applyPageJson(field, text, empty) {
      try {
        const parsed = JSON.parse(text || JSON.stringify(empty));
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) throw new Error(`${field} must be a JSON object.`);
        if (Object.keys(parsed).length) this.draft[field] = parsed;
        else delete this.draft[field];
        this.pageJsonError = '';
      } catch (e) {
        this.pageJsonError = e.message;
      }
    },
    applyFilters() {
      try {
        const parsed = JSON.parse(this.filtersText || '[]');
        if (!Array.isArray(parsed)) throw new Error('Filters must be a JSON list.');
        this.draft.filters = parsed;
        this.filtersError = '';
      } catch (e) {
        this.filtersError = e.message;
      }
    },
    addPlacement() {
      const base = 'placement';
      let n = (this.draft.placements || []).length + 1;
      while (this.draft.placements.some((p) => p.id === `${base}-${n}`)) n += 1;
      const first = this.widgetList.find((w) => w.slug === 'heading') || this.widgetList[0];
      const placement = {
        id: `${base}-${n}`, widget: first ? first.slug : 'heading', inputs: {},
        config: first && first.slug === 'heading' ? { text: 'New section', level: 2 } : {},
        layout: { x: 24, y: canvasHeight(this.draft) - 24, w: 480, h: 240 }, intent: '',
      };
      this.draft.placements.push(placement);
      this.selectedId = placement.id;
    },
    setLook(key, raw) {
      const p = this.selected;
      if (!p) return;
      const style = { ...(p.style || {}) };
      const numeric = ['border_width', 'border_radius', 'padding'].includes(key);
      const value = numeric ? (raw === '' ? null : Number(raw)) : String(raw || '').trim();
      if (value === null || value === '') delete style[key];
      else style[key] = value;
      if (Object.keys(style).length) p.style = style;
      else delete p.style;
    },
    removePlacement(id) {
      this.draft.placements = this.draft.placements.filter((p) => p.id !== id);
      this.selectedId = null;
    },
    startDrag(event, p, mode) {
      this.selectedId = p.id;
      this.drag = { id: p.id, mode, startX: event.clientX, startY: event.clientY, layout: { ...p.layout } };
    },
    onDrag(event) {
      if (!this.drag) return;
      const p = this.draft.placements.find((x) => x.id === this.drag.id);
      if (!p) return;
      const dx = (event.clientX - this.drag.startX) / this.scale;
      const dy = (event.clientY - this.drag.startY) / this.scale;
      const snap = (v) => Math.max(0, Math.round(v / 8) * 8);
      if (this.drag.mode === 'move') {
        p.layout.x = snap(this.drag.layout.x + dx);
        p.layout.y = snap(this.drag.layout.y + dy);
      } else {
        p.layout.w = Math.max(80, snap(this.drag.layout.w + dx));
        p.layout.h = Math.max(48, snap(this.drag.layout.h + dy));
      }
    },
    endDrag() {
      this.drag = null;
    },
    async save() {
      this.applyConfig();
      this.applyFilters();
      this.applyPageJson('implicit', this.implicitPageText, {});
      this.applyPageJson('sources', this.sourcesText, {});
      if (this.selected) {
        this.applyPlacementJson('implicit', this.implicitText, {});
        this.applyPlacementJson('filters', this.widgetFiltersText, []);
      }
      if (this.configError || this.filtersError || this.pageJsonError || this.placementJsonError) return;
      this.saving = true;
      this.saveError = '';
      try {
        const body = { spec: this.draft, category: this.draftCategory };
        if (this.subject) body.shared = this.draftShared;
        else body.stem = this.draftStem;
        const { data } = await axios.put(`/api/impact_analytics/agent-pages/${this.pageId}/spec/`, body);
        this.$emit('saved', data);
      } catch (err) {
        this.saveError = err?.response?.data?.error || 'The page could not be saved.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.pe { display: flex; flex-direction: column; gap: 10px; }
.pe-bar { display: flex; justify-content: flex-end; gap: 6px; }
.pe-btn { font-size: 12.5px; padding: 5px 10px; border: 1px solid #dad8d2; border-radius: 6px; background: #fff; color: #1a1918; cursor: pointer; }
.pe-primary { background: #EF6C3B; border-color: #EF6C3B; color: #fff; }
.pe-danger { color: #C0392B; margin-top: 8px; }
.pe-error { font-size: 12px; color: #c23a28; }
.pe-stage { display: flex; gap: 12px; align-items: flex-start; }
.pe-stagebg { flex: 1; min-width: 0; }
.pe-viewport { position: relative; overflow: hidden; }
.pe-canvas { position: absolute; left: 0; top: 0; transform-origin: 0 0; }
.pe-place { position: absolute; outline: 1px dashed #dad8d2; }
.pe-selected { outline: 2px solid #EF6C3B !important; }
.pe-grip { position: absolute; left: 0; top: -18px; height: 18px; line-height: 18px; padding: 0 6px; font-size: 10.5px; color: #fff; background: #EF6C3B; border-radius: 4px 4px 0 0; cursor: move; user-select: none; z-index: 2; }
.pe-resize { position: absolute; right: -4px; bottom: -4px; width: 12px; height: 12px; background: #EF6C3B; border-radius: 3px; cursor: nwse-resize; z-index: 2; }
.pe-panel { width: 300px; flex: none; background: #fff; border: 1px solid #e6e4df; border-radius: 8px; padding: 12px; display: flex; flex-direction: column; gap: 8px; font-size: 12.5px; }
.pe-panel-title { font-weight: 600; color: #1a1918; }
.pe-panel-sub { font-weight: 600; color: #56534d; font-size: 11.5px; text-transform: uppercase; letter-spacing: 0.03em; margin-top: 6px; padding-top: 8px; border-top: 1px solid #f0efec; }
.pe-field { display: flex; flex-direction: column; gap: 3px; color: #56534d; }
.pe-field input, .pe-field select, .pe-field textarea, .pe-input select { font-size: 12.5px; padding: 5px 6px; border: 1px solid #dad8d2; border-radius: 6px; background: #fff; font-family: inherit; }
.pe-field textarea { font-family: 'IBM Plex Mono', ui-monospace, monospace; font-size: 11.5px; }
.pe-row { display: flex; gap: 6px; }
.pe-color { display: flex; gap: 6px; align-items: center; }
.pe-color input[type="color"] { width: 32px; height: 28px; padding: 2px; }
.pe-color input[type="text"] { flex: 1; min-width: 0; }
.pe-num { flex: 1; }
.pe-input { display: flex; flex-direction: column; gap: 3px; }
.pe-input-name { color: #56534d; }
.pe-hint { font-size: 11px; color: #8b877f; }
.pe-check { flex-direction: row; align-items: center; gap: 6px; }
</style>
